/**
 * Builds lookup maps from EV coverage API payloads: `requestedServiceType`
 * `{ code, value }` arrays when present, plus PNT/Postgres fallbacks from
 * `patient_responsibility` and `extracted_coverage.coverage` rows.
 */
import type { CoverageResult } from "../../../data-model/medicalPaOrdersModel";

/**
 * Collects `requestedServiceType` objects from known locations on a coverage result.
 *
 * @param result - One Postgres coverage_result row
 * @returns Normalized code/value pairs with non-empty codes
 */
const collectRequestedServiceTypePairsFromResult = (
  result: CoverageResult,
): Array<{ code: string; value: string }> => {
  const containers: Array<Record<string, unknown> | undefined> = [
    result.coverage_request,
    result.raw_response,
    result.extracted_coverage,
    result.coverage_payload,
  ];

  const pairs: Array<{ code: string; value: string }> = [];

  for (const obj of containers) {
    if (!obj || typeof obj !== "object") continue;
    const raw = obj.requestedServiceType;
    if (!Array.isArray(raw)) continue;

    for (const item of raw) {
      if (item === null || typeof item !== "object") continue;
      const rec = item as Record<string, unknown>;
      const code = String(rec.code ?? "").trim();
      const value = String(rec.value ?? "").trim();
      if (code) {
        pairs.push({ code, value });
      }
    }
  }

  return pairs;
};

/**
 * Reads `service_type` + `service_type_name` from the coverage_result
 * `patient_responsibility` blob (common for pnt_api / Fidelis rows).
 *
 * @param result - One Postgres coverage_result row
 * @returns Zero or one code/label pair when both fields are present
 */
const collectPatientResponsibilityServiceTypePairs = (
  result: CoverageResult,
): Array<{ code: string; value: string }> => {
  const pr = result.patient_responsibility;
  if (!pr || typeof pr !== "object") return [];

  const rec = pr as Record<string, unknown>;
  const code = String(rec.service_type ?? "").trim();
  const value = String(rec.service_type_name ?? "").trim();
  if (!code || !value) return [];
  return [{ code, value }];
};

/**
 * Derives code/label pairs from structured benefit rows under
 * `extracted_coverage.coverage` (parallel `service_type_codes` / `service_types`).
 *
 * @param result - One Postgres coverage_result row
 * @returns Pairs for each benefit row that includes both arrays
 */
const collectExtractedCoverageServiceTypePairs = (
  result: CoverageResult,
): Array<{ code: string; value: string }> => {
  const ec = result.extracted_coverage;
  if (!ec || typeof ec !== "object") return [];

  const rawCoverage = (ec as Record<string, unknown>).coverage;
  if (!Array.isArray(rawCoverage)) return [];

  const pairs: Array<{ code: string; value: string }> = [];

  for (const row of rawCoverage) {
    if (row === null || typeof row !== "object") continue;
    const rec = row as Record<string, unknown>;
    const codes = rec.service_type_codes;
    const types = rec.service_types;
    if (!Array.isArray(codes) || !Array.isArray(types)) continue;

    const n = Math.min(codes.length, types.length);
    for (let i = 0; i < n; i += 1) {
      const code = String(codes[i] ?? "").trim();
      const value = String(types[i] ?? "").trim();
      if (code && value) {
        pairs.push({ code, value });
      }
    }
  }

  return pairs;
};

/**
 * Collects all known service-type code → label pairs for one result, in merge
 * priority order (explicit API list first, then PR row, then extracted benefits).
 *
 * @param result - One Postgres coverage_result row
 * @returns Ordered pairs; first wins when merged into a map by code
 */
const collectAllServiceTypeLabelPairsFromResult = (
  result: CoverageResult,
): Array<{ code: string; value: string }> => {
  return [
    ...collectRequestedServiceTypePairsFromResult(result),
    ...collectPatientResponsibilityServiceTypePairs(result),
    ...collectExtractedCoverageServiceTypePairs(result),
  ];
};

/**
 * Merges service-type label sources from all coverage results into one map
 * (code → display value). The first non-empty label for each code wins; later
 * duplicates are ignored.
 *
 * @param coverageResults - Coverage result rows from the coverage response
 * @returns Readonly map from service type code to human-readable value
 */
export const buildRequestedServiceTypeLabelMap = (
  coverageResults: CoverageResult[] | undefined,
): ReadonlyMap<string, string> => {
  const map = new Map<string, string>();
  if (!coverageResults?.length) {
    return map;
  }

  for (const result of coverageResults) {
    for (const { code, value } of collectAllServiceTypeLabelPairsFromResult(
      result,
    )) {
      if (value && !map.has(code)) {
        map.set(code, value);
      }
    }
  }

  return map;
};
